import * as React from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { Header } from '@/components/layout/header'
import { DataTable } from '@/components/ui/data-table'
import { getInvoiceColumns } from '@/components/invoices/invoice-columns'
import { InvoiceToolbar, type DateFilter } from '@/components/invoices/invoice-toolbar'
import { InvoiceSheet } from '@/components/invoices/invoice-sheet'
import { useInvoices } from '@/hooks/useInvoices'
import { useBranch, type BranchId } from '@/providers/branch-provider'
import type { Invoice } from '@/lib/api-client'
import type { SortingState, RowSelectionState, VisibilityState } from '@tanstack/react-table'
import {
  useReactTable,
  getCoreRowModel,
  type PaginationState,
} from '@tanstack/react-table'

export const Route = createFileRoute('/invoices')({
  component: InvoicesPage,
})

// Branch code to ID mapping
const BRANCH_IDS: Record<BranchId, number | undefined> = {
  all: undefined,
  'shah-alam': 1,
  putrajaya: 2,
  bangi: 3,
  'kuala-lumpur': 4,
}

function InvoicesPage() {
  const { branchId } = useBranch()

  // Table state
  const [page, setPage] = React.useState(1)
  const [pageSize] = React.useState(20)
  const [sorting, setSorting] = React.useState<SortingState>([])
  const [rowSelection, setRowSelection] = React.useState<RowSelectionState>({})
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({})

  // Search and filters
  const [search, setSearch] = React.useState('')
  const [debouncedSearch, setDebouncedSearch] = React.useState('')
  const [dateFilter, setDateFilter] = React.useState<DateFilter>('week') // Default: This Week

  // Sheet state
  const [sheetOpen, setSheetOpen] = React.useState(false)
  const [selectedInvoiceId, setSelectedInvoiceId] = React.useState<number | null>(null)

  // Debounce search
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search)
      setPage(1) // Reset to first page on search
    }, 300)
    return () => clearTimeout(timer)
  }, [search])

  // Reset page when filters change
  React.useEffect(() => {
    setPage(1)
  }, [branchId, dateFilter])

  // Get sort params from state
  const sortBy = sorting[0]?.id as
    | 'invoiceNo'
    | 'invoiceDate'
    | 'grandTotal'
    | 'patientName'
    | 'createdAt'
    | undefined
  const sortOrder = sorting[0]?.desc ? 'desc' : 'asc'

  // Fetch invoices
  const { data, isLoading } = useInvoices({
    branchId: BRANCH_IDS[branchId],
    search: debouncedSearch || undefined,
    page,
    pageSize,
    sortBy,
    sortOrder: sorting.length > 0 ? sortOrder : undefined,
    dateFilter: dateFilter !== 'all' ? dateFilter : undefined,
  })

  // Handlers
  const handleView = React.useCallback((invoice: Invoice) => {
    setSelectedInvoiceId(invoice.id)
    setSheetOpen(true)
  }, [])

  const handleRowClick = React.useCallback((invoice: Invoice) => {
    handleView(invoice)
  }, [handleView])

  // Memoize columns to prevent re-renders
  const columns = React.useMemo(
    () => getInvoiceColumns({ onView: handleView }),
    [handleView]
  )

  // Create table instance
  const table = useReactTable({
    data: data?.data ?? [],
    columns,
    pageCount: data?.pageCount ?? -1,
    state: {
      sorting,
      rowSelection,
      columnVisibility,
      pagination: {
        pageIndex: page - 1,
        pageSize,
      },
    },
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    onColumnVisibilityChange: setColumnVisibility,
    onPaginationChange: (updater) => {
      const newPagination = typeof updater === 'function'
        ? updater({ pageIndex: page - 1, pageSize })
        : updater
      setPage(newPagination.pageIndex + 1)
    },
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
  })

  return (
    <div className="flex flex-col h-full">
      <Header
        title="Invoices"
        subtitle={data ? `${data.total.toLocaleString()} invoices` : 'Loading...'}
      />

      <div className="flex-1 p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4">
        {/* Toolbar */}
        <InvoiceToolbar
          search={search}
          onSearchChange={setSearch}
          dateFilter={dateFilter}
          onDateFilterChange={setDateFilter}
          table={table}
        />

        {/* Table */}
        <DataTable
          columns={columns}
          data={data?.data ?? []}
          isLoading={isLoading}
          pageCount={data?.pageCount}
          pageIndex={page - 1}
          pageSize={pageSize}
          onPaginationChange={({ pageIndex }) => setPage(pageIndex + 1)}
          sorting={sorting}
          onSortingChange={setSorting}
          rowSelection={rowSelection}
          onRowSelectionChange={setRowSelection}
          onRowClick={handleRowClick}
          emptyMessage="No invoices found. Try adjusting your search or filters."
        />
      </div>

      {/* Invoice Detail Sheet */}
      <InvoiceSheet
        invoiceId={selectedInvoiceId}
        open={sheetOpen}
        onOpenChange={setSheetOpen}
      />
    </div>
  )
}
